import React from "react";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import DateRangeCalendar from "screens/Customers/component/DateRangeCalendar";
import dayjs from "dayjs";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import CustomLabel from "components/CustomLabel";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { GetPayrollReport } from "../logic/PayrollReportLogic";
import ImportExportOptions from "components/ImportExportOptions";

// Helper function to format dates
const formatDateSubtractDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

const PayrollReport = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openEmployeeDialog, setOpenEmployeeDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payrollData, setPayrollData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);

  // Transform API data to match the expected format
  const transformPayrollData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    
    // Group data by employee
    const groupedData = apiData.reduce((acc, item) => {
      const employeeKey = `${item.employeeID}-${item.employeeName}`;
      if (!acc[employeeKey]) {
        acc[employeeKey] = {
          id: item.employeeID,
          employeeID: item.employeeID,
          employee: item.employeeName,
          team: item.teams || 'N/A',
          skill: item.skillType || 'N/A',
          rate: `$${item.hourlyPayRate.toFixed(2)}`,
          regularHours: 0,
          otHours: 0,
          netHours: 0,
          totalPay: 0,
          details: []
        };
      }
      
      // Add to totals
      acc[employeeKey].regularHours += item.regularHours || 0;
      acc[employeeKey].otHours += item.otHours || 0;
      acc[employeeKey].netHours += item.netHours || 0;
      acc[employeeKey].totalPay += (item.regularHours * item.hourlyPayRate) + (item.otHours * item.hourlyPayRate * 1.5);
      
      // Add detail record
      acc[employeeKey].details.push({
        date: item.workDate ? dayjs(item.workDate).format('MM/DD/YY') : 'N/A',
        clockIn: item.clockIn || 'N/A',
        clockOut: item.clockOut || 'N/A',
        breakHours: item.breakHours || 0,
        netHours: item.netHours || 0,
        regularHours: item.regularHours || 0,
        otHours: item.otHours || 0
      });
      
      return acc;
    }, {});
    
    // Convert to array and format display values
    return Object.values(groupedData).map(employee => ({
      ...employee,
      regularHours: `${employee.regularHours.toFixed(2)}h`,
      overtimeHours: `${employee.otHours.toFixed(2)}h`,
      totalHours: `${employee.netHours.toFixed(2)}h`,
      totalPay: `$${employee.totalPay.toFixed(2)}`,
      regularHoursDetails: employee.details.map(detail => ({
        date: detail.date,
        clockIn: detail.clockIn,
        clockOut: detail.clockOut,
        breaks: `${detail.breakHours.toFixed(2)}h`,
        hrsWorked: `${detail.regularHours.toFixed(2)}h`
      })),
      overtimeHoursDetails: employee.details.map(detail => ({
        date: detail.date,
        overtimeHours: `${detail.otHours.toFixed(2)}h`
      })),
      totalManHoursDetails: employee.details.map(detail => ({
        date: detail.date,
        reg: `${detail.regularHours.toFixed(2)}h`,
        overtimeHours: `${detail.otHours.toFixed(2)}h`,
        total: `${detail.netHours.toFixed(2)}h`
      }))
    }));
  };

  const columns = [
    {
      field: "employee",
      headerName: "Employee",
      flex: 1.5,
      renderCell: (params) => (
        <Box
          sx={{
            textDecoration: "underline",
            cursor: "pointer",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
          }}
          onClick={() => {
            setSelectedEmployee(params.row);
            setOpenEmployeeDialog(true);
          }}
        >
          {params.value}
        </Box>
      ),
    },
    { field: "team", headerName: "Team", flex: 1 },
    { field: "skill", headerName: "Skill Type", flex: 1 },
    { field: "regularHours", headerName: "Regular Hours", flex: 1 },
    { field: "overtimeHours", headerName: "Overtime Hours", flex: 1 },
    { field: "totalHours", headerName: "Total Hours", flex: 1 },
    { field: "rate", headerName: "Hourly Rate", flex: 1 },
    { field: "totalPay", headerName: "Total Pay", flex: 1 },
  ];

  // Fetch payroll data
  const fetchPayrollData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await GetPayrollReport(startDate, endDate, navigate);
      if (response && !response.isError && response.result) {
        const transformedData = transformPayrollData(response.result);
        setPayrollData(transformedData);
        setFilteredData(transformedData);
        
        // Extract unique employee names for filter dropdown
        const uniqueEmployees = [...new Set(transformedData.map(item => item.employee))]
          .sort()
          .map(name => ({ value: name, label: name }));
        setEmployeeOptions(uniqueEmployees);
      } else {
        setError(response?.message || 'Failed to fetch payroll data');
      }
    } catch (err) {
      setError('An error occurred while fetching payroll data');
      console.error('Payroll fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchPayrollData();
  }, []);

  // Handle date range change
  const handleDateRangeChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Handle run filter
  const handleRunFilter = () => {
    fetchPayrollData();
  };

  // Handle employee filter change
  const handleEmployeeFilterChange = (event) => {
    const selectedEmployeeName = event.target.value;
    setSelectedEmployeeFilter(selectedEmployeeName);
    
    if (selectedEmployeeName === '') {
      // Show all data if no employee selected
      setFilteredData(payrollData);
    } else {
      // Filter data by selected employee
      const filtered = payrollData.filter(item => item.employee === selectedEmployeeName);
      setFilteredData(filtered);
    }
  };

  // For demonstration purposes - replace with your actual permission logic
  const hasCommunicationGetPermission = false;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (option) => {
    setAnchorEl(null);
    if (option) {
      console.log("Export as:", option);
      // 🔹 Call your export logic here (CSV/XLS/PDF)
    }
  };

 const handleNavigation = (to) => {
   
      navigate(to);
    
  };

      const handlePageBack = () => {
    handleNavigation('/reports');    
  }

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Payroll Report"
    >
      <Card
        sx={{
          p: 3,
          boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          height: "100%",
          width: "100%",
          overflowX: "auto",
          mx: "auto",
          mt: 4,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              p: 3,
              bgcolor: "white",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h4">Payroll Report</Typography>
                <Typography variant="body1" color="text.secondary">
                  Employee hours, overtime, and pay calculations
                </Typography>
              </Box>
              {/* <Button
                variant="contained"
                color="main"
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
              >
                Export
              </Button> */}
              <ImportExportOptions 
                handleOpenBulkImportDlg={()=>{}}                          
                data={filteredData}
                module="Payroll"
              />   

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose(null)}
              >
                <MenuItem onClick={() => handleClose("csv")}>CSV</MenuItem>
                <MenuItem onClick={() => handleClose("xls")}>XLS</MenuItem>
                <MenuItem onClick={() => handleClose("pdf")}>PDF</MenuItem>
              </Menu>
            </Box>
            <Divider sx={{ mb: 5 }} />
            {/* Expandable Filters */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "#F8FAFC", mt: 2 }}
              >
                <Typography variant="h5">Filters</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: "#F8FAFC" }}>
                {/* First Row */}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    // justifyContent: "space-between",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <CustomTextField
                      select
                      size="small"
                      label="Employee Name"
                      value={selectedEmployeeFilter}
                      onChange={handleEmployeeFilterChange}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected) {
                            return "All Employees"; 
                          }
                          return selected;
                        },
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          width: "100% !important",
                          border: "1px solid #D9D9D9",
                          boxShadow: "none",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          width: "100% !important",
                        },
                        "& .MuiInputBase-input": {
                          width: "100% !important",
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>All Employees</em>
                      </MenuItem>
                      {employeeOptions.map((employee) => (
                        <MenuItem key={employee.value} value={employee.value}>
                          {employee.label}
                      </MenuItem>
                      ))}
                    </CustomTextField>

                    {/* Commented out Job Type filter
                    <CustomTextField
                      select
                      size="small"
                      label="Job Type"
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected) {
                            return "Select Type";
                          }
                          const optionMap = {
                            full_time: "Full Time",
                            part_time: "Part Time",
                          };
                          return optionMap[selected] || selected;
                        },
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          width: "100% !important",
                          border: "1px solid #D9D9D9",
                          boxShadow: "none",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          width: "100% !important",
                        },
                        "& .MuiInputBase-input": {
                          width: "100% !important",
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Job Type</em>
                      </MenuItem>
                      <MenuItem value="full_time">Full Time</MenuItem>
                      <MenuItem value="part_time">Part Time</MenuItem>
                    </CustomTextField>
                    */}
                  </Box>
                  
                  {/* 
                  Commented out filters for future reference:
                  
                  Employee Role Filter:
                  - CustomTextField with select for manager/technician roles
                  
                  Job Type Filter:  
                  - CustomTextField with select for full_time/part_time
                  
                  Job Status Filter:
                  - CustomTextField with select for active/inactive status
                  */}
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomLabel label="Date Range" />
                    <DateRangeCalendar
                      initialStartDate={dayjs(startDate)}
                      initialEndDate={dayjs(endDate)}
                      onDateChange={handleDateRangeChange}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        order: { xs: 4, sm: 3 },
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    flexWrap: "wrap",
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="main"
                    size="small"
                    onClick={handleRunFilter}
                    disabled={loading}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    {loading ? 'Loading...' : 'Run Filter'}
                  </Button>
                </Box>

                {/* Third Row - Run Filter button aligned right */}
              </AccordionDetails>
            </Accordion>

            {/* Error Display */}
            {/* {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
            )} */}

            {/* Loading State */}
            {/* {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
                <CircularProgress />
              </Box>
            )} */}

            {/* DataGrid */}
            <Box sx={{ height: 400, width: "100%", mt: 5 }}>
              <DataGridPro
                rows={filteredData}
                columns={columns}
                loading={loading}
                disableRowSelectionOnClick
                // pageSizeOptions={[5, 10, 25, 50]}
                // initialState={{
                //   pagination: { paginationModel: { pageSize: 10 } },
                // }}
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    textTransform: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    textTransform: "none",
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        {loading ? 'Loading payroll data...' : 'No payroll data found for the selected date range'}
                      </Typography>
                    </Box>
                  ),
                  footer: () => null
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Employee Details Dialog */}
      <Dialog
        open={openEmployeeDialog}
        onClose={() => setOpenEmployeeDialog(false)}
        fullWidth
        maxWidth="md"
        sx={{ "& .MuiDialog-paper": { maxHeight: "85vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">
            {selectedEmployee?.employee} - Hours Details
          </Typography>
          <IconButton onClick={() => setOpenEmployeeDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedEmployee && (
            <Box sx={{ width: "100%" }}>
              {/* Total Hours Section */}
              <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                Total Hours
              </Typography>
              <Box sx={{ height: 200, width: "100%", mb: 3 }}>
                <DataGridPro
                  rows={selectedEmployee.totalManHoursDetails.map(
                    (item, index) => ({ id: index, ...item })
                  )}
                  columns={[
                    { field: "date", headerName: "Date", flex: 1 },
                    { field: "reg", headerName: "Regular Hours", flex: 1 },
                    {
                      field: "overtimeHours",
                      headerName: "Overtime Hours",
                      flex: 1,
                    },
                    { field: "total", headerName: "Total", flex: 1 },
                  ]}
                  // pageSizeOptions={[5, 10]}
                  // initialState={{
                  //   pagination: { paginationModel: { pageSize: 5 } },
                  // }}
                  slots={{
                    footer: () => null
                  }}
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textTransform: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      textTransform: "none",
                    },
                  }}
                />
              </Box>

              {/* Regular Hours Section */}
              <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                Regular Hours
              </Typography>
              <Box sx={{ height: 200, width: "100%", mb: 3 }}>
                <DataGridPro
                  rows={selectedEmployee.regularHoursDetails.map(
                    (item, index) => ({ id: index, ...item })
                  )}
                  columns={[
                    { field: "date", headerName: "Date", flex: 1 },
                    { field: "clockIn", headerName: "Clock In", flex: 1 },
                    { field: "clockOut", headerName: "Clock Out", flex: 1 },
                    { field: "breaks", headerName: "Breaks", flex: 1 },
                    { field: "hrsWorked", headerName: "Hrs Worked", flex: 1 },
                  ]}
                  // pageSizeOptions={[5, 10]}
                  // initialState={{
                  //   pagination: { paginationModel: { pageSize: 5 } },
                  // }}
                  slots={{
                    footer: () => null
                  }}
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textTransform: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      textTransform: "none",
                    },
                  }}
                />
              </Box>

              {/* Overtime Hours Section */}
              <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                Overtime Hours
              </Typography>
              <Box sx={{ height: 200, width: "100%", mb: 3 }}>
                <DataGridPro
                  rows={selectedEmployee.overtimeHoursDetails.map(
                    (item, index) => ({ id: index, ...item })
                  )}
                  columns={[
                    { field: "date", headerName: "Date", flex: 1 },
                    {
                      field: "overtimeHours",
                      headerName: "Overtime Hours",
                      flex: 1,
                    },
                  ]}
                  // pageSizeOptions={[5, 10]}
                  // initialState={{
                  //   pagination: { paginationModel: { pageSize: 5 } },
                  // }}
                  slots={{
                    footer: () => null
                  }}
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textTransform: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      textTransform: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </UserLayout>
  );
};

export default PayrollReport;